import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { projectsApi, unwrap } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Card, Empty, Icon, Loader, StatusPill } from "../components/ui";
import { KhataRunning, ProjectKhataSummary } from "../components/KhataSummary";
import { ProofLine } from "../components/PaymentFields";
import { emptyProjectKhata, isCreditPayment } from "../utils/finance";
import { entryLabel, formatCount, formatDate, formatMoney } from "../utils/format";

const entryTone = (entry) => {
  if (entry.kind === "down_payment" || entry.kind === "installment") return "plus";
  if (entry.kind === "expense" && !isCreditPayment(entry.paymentType)) return "minus";
  return "";
};

export default function ProjectKhata() {
  const { id } = useParams();
  const { query } = useBuilderQuery();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      try {
        setData({ ...unwrap(await projectsApi.khata(id, query)), fullKhata: true });
      } catch (error) {
        if (error.statusCode !== 404) throw error;
        const project = unwrap(await projectsApi.get(id, query));
        setData({
          project,
          khata: project?.khata || emptyProjectKhata(),
          vendors: [],
          sales: [],
          entries: [],
          fullKhata: false,
        });
      }
    } catch (error) {
      toast.error(error.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, query.builderId]);

  if (loading) return <Loader />;
  if (!data) return <Empty title="Project not found" text="Go back to projects." />;

  const project = data.project || {};
  const khata = data.khata || emptyProjectKhata();
  const vendors = data.vendors || [];
  const sales = data.sales || [];
  const entries = data.entries || [];

  return (
    <div className="stack">
      <Link to="/projects" className="back-link">
        <Icon name="back" size={18} /> Back to projects
      </Link>

      <header className="page-head">
        <div>
          <p className="eyebrow">Project khata</p>
          <h1>{project.name}</h1>
          <p className="muted">{project.location || "No location"}</p>
        </div>
        <Link to={`/projects/${project.id || id}/statement`}>Statement</Link>
      </header>

      <ProjectKhataSummary khata={khata} />

      {!data.fullKhata ? (
        <div className="banner">Could not load the full project khata. Showing project totals only.</div>
      ) : null}

      {data.fullKhata ? (
        <>
      <Card>
        <h2>Vendors on this project</h2>
        {vendors.length === 0 ? (
          <Empty title="No vendor spend yet" text="Expenses with a vendor will show here." />
        ) : (
          <div className="list">
            {vendors.map((vendor) => (
              <Link key={vendor.id} className="list-row" to={`/vendors/${vendor.id}`}>
                <div>
                  <b>{vendor.name || "Vendor"}</b>
                  <span>
                    {vendor.phone || "No phone"}
                    {vendor.expenseCount > 0 ? ` · ${formatCount(vendor.expenseCount, "expense")}` : ""}
                  </span>
                  <span className="khata-running">
                    Cash {formatMoney(vendor.cash)} · Bank {formatMoney(vendor.bank)} · Udhar{" "}
                    {formatMoney(vendor.credit)}
                  </span>
                </div>
                <div className="list-money">
                  <small>Due {formatMoney(vendor.payable)}</small>
                  <strong>{formatMoney(vendor.totalBill)}</strong>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <h2>Sales</h2>
        {sales.length === 0 ? (
          <Empty title="No sales yet" text="Add a sale in this project to see it here." />
        ) : (
          <div className="list">
            {sales.map((sale) => (
              <Link key={sale.id} className="list-row" to={`/sales/${sale.id}`}>
                <div>
                  <div className="sale-card-top">
                    <b>{sale.buyerName}</b>
                    <StatusPill status={sale.status} />
                  </div>
                  <span>
                    {sale.propertyName}
                    {sale.date ? ` · ${formatDate(sale.date)}` : ""}
                  </span>
                </div>
                <div className="list-money">
                  <small>
                    Received {formatMoney(sale.receivedAmount)} · Remaining {formatMoney(sale.remainingAmount)}
                  </small>
                  <strong>{formatMoney(sale.totalAmount)}</strong>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <h2>Khata history</h2>
        {entries.length === 0 ? (
          <Empty title="No entries yet" text="Expenses, down payments and installments will show here." />
        ) : (
          <div className="list">
            {entries.map((entry) => {
              const saleId = entry.sale?.id;
              const vendorId = entry.vendor?.id;
              const href = saleId ? `/sales/${saleId}` : vendorId ? `/vendors/${vendorId}` : null;
              const RowTag = href ? Link : "div";
              const extra = entry.vendor?.name || "";
              return (
                <RowTag key={`${entry.kind}-${entry.id}`} className="list-row" to={href || undefined}>
                  <div>
                    <b>{entry.title || entryLabel(entry.kind)}</b>
                    <span>
                      {formatDate(entry.date)}
                      {extra ? ` · ${extra}` : ""}
                      {entry.note ? ` · ${entry.note}` : ""}
                    </span>
                    <ProofLine
                      paymentType={entry.paymentType}
                      referenceNumber={entry.referenceNumber}
                      image={entry.image}
                      bank={entry.bank}
                    />
                    <KhataRunning running={entry.running} variant="project" />
                  </div>
                  <strong className={entryTone(entry)}>
                    {entry.kind === "expense" && isCreditPayment(entry.paymentType) ? "Udhar " : ""}
                    {formatMoney(entry.amount)}
                  </strong>
                </RowTag>
              );
            })}
          </div>
        )}
      </Card>
        </>
      ) : null}
    </div>
  );
}
